import { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const iso = (d) => d.toISOString().slice(0, 10);

function daysBetween(startISO, endISO) {
  const start = new Date(startISO + "T00:00:00Z");
  const end = new Date(endISO + "T00:00:00Z");
  const out = [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(iso(new Date(d)));
  }
  return out;
}

function SummaryCell({ label, value }) {
  return (
    <div className="rounded-md bg-white px-2 py-1">
      <div className="text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared Sync FullBay dialog with client-side day-by-day chunking
//
// Props:
//   companies         list of company docs (used when no preset).
//   presetCompanyId   if provided, dropdown is locked to this company. When
//                     omitted, the user picks from `companies` (eligible =
//                     active + mapped to fullbay_customer_title).
//   open, onOpenChange
//   onComplete(summary)  fired after the whole day range finishes.
// ---------------------------------------------------------------------------
export default function SyncFullBayDialog({
  companies = [], presetCompanyId, open, onOpenChange, onComplete,
}) {
  const eligible = companies.filter((c) => c.active && c.fullbay_customer_title);
  const [companyId, setCompanyId] = useState(presetCompanyId || eligible[0]?.id || "");
  const today = iso(new Date());
  const default3 = iso(new Date(Date.now() - 3 * 86400000));
  const [start, setStart] = useState(default3);
  const [end, setEnd] = useState(today);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: null, failed: [] });
  const [summary, setSummary] = useState(null);
  const [jobId, setJobId] = useState(null);
  // Heartbeat freshness for stalled detection (ISO string from backend).
  const [lastHeartbeat, setLastHeartbeat] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [aborting, setAborting] = useState(false);

  // Tick every 5s while a sync is running so the stalled banner's
  // "Xs/Xm ago" label + threshold check stay fresh.  Fast enough to
  // flip within a few seconds of the 60s orphan threshold.
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNowTick(Date.now()), 5000);
    return () => clearInterval(t);
  }, [running]);

  // Show the stalled banner + Abort as soon as the heartbeat is older
  // than 60s.  Live supervisors write heartbeats every ~2s, so 60s is
  // a very safe threshold with no false positives — and it matches the
  // backend's orphan-detection window in `request_abort`.
  const stalled = (() => {
    if (!running || !lastHeartbeat) return null;
    const hbMs = new Date(lastHeartbeat).getTime();
    if (isNaN(hbMs)) return null;
    const ageMs = nowTick - hbMs;
    if (ageMs < 60 * 1000) return null;
    const secs = Math.floor(ageMs / 1000);
    return secs >= 120
      ? { label: `${Math.floor(secs / 60)}m ago` }
      : { label: `${secs}s ago` };
  })();

  // localStorage key for resume-on-refresh (per company). Nice-to-have — if
  // the user reloads mid-sync we reattach to the running job doc instead of
  // firing a duplicate.
  const activeJobKey = companyId ? `er:sync:activeJob:${companyId}` : null;

  // If a preset arrives (or changes), lock the picker to it.
  useEffect(() => {
    if (presetCompanyId) setCompanyId(presetCompanyId);
  }, [presetCompanyId]);

  // If no preset and companies load in later, hydrate a default.
  useEffect(() => {
    if (presetCompanyId) return;
    if (!companyId && eligible[0]) setCompanyId(eligible[0].id);
  }, [companyId, eligible, presetCompanyId]);

  // Reset progress/summary each time the dialog opens.
  useEffect(() => {
    if (open) {
      setProgress({ done: 0, total: 0, current: null, failed: [] });
      setSummary(null);
      // Resume-on-refresh: if a job is still running for this company,
      // reattach to it silently. Users who reload the page mid-sync see
      // progress pick up where it left off instead of getting a fresh
      // dialog with no state.
      if (activeJobKey) {
        const savedId = localStorage.getItem(activeJobKey);
        if (savedId) {
          resumePolling(savedId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeJobKey]);

  const presetCompany = presetCompanyId
    ? companies.find((c) => c.id === presetCompanyId)
    : null;

  // ---------- Polling helpers ----------
  // Map a job doc to the flat progress state the UI renders.
  const applyJob = (job) => {
    const p = job.progress || {};
    const r = job.report || {};
    setProgress({
      done: p.days_done || 0,
      total: p.total_days || 0,
      current: p.current_day || null,
      failed: (r.failed_days || []).map((day) => ({ day, error: "sync failed for this day" })),
    });
    setSummary({
      invoices_new: r.invoices_new || 0,
      invoices_updated: r.invoices_updated || 0,
      corrections_new: r.corrections_new || 0,
      drivers_resolved: r.drivers_resolved || 0,
    });
    setLastHeartbeat(job.last_heartbeat_at || job.updated_at || null);
  };

  // Trigger cooperative abort on the current running job.  Runner exits
  // at the next day boundary (typically < a few seconds after this
  // returns; up to the per-day watchdog ceiling when a day is stuck).
  const abortJob = async () => {
    if (!jobId || aborting) return;
    setAborting(true);
    try {
      await api.post(`/equipment-repairs/sync/${jobId}/abort`);
      toast.success("Abort requested — cancelling in-flight request…");
    } catch (e) {
      toast.error(`Abort failed: ${extractError(e)}`);
    } finally {
      setAborting(false);
    }
  };

  // Poll a job every 2s until it reaches a terminal status. Awaits the
  // terminal doc so callers can chain on completion (e.g. show toast).
  const pollUntilDone = async (id) => {
    const TERMINAL = new Set(["done", "failed", "partial", "aborted"]);
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let job;
      try {
        const { data } = await api.get(`/equipment-repairs/sync/${id}`);
        job = data;
      } catch (e) {
        toast.error(`Sync status check failed: ${extractError(e)}`);
        return null;
      }
      applyJob(job);
      if (TERMINAL.has(job.status)) return job;
      await new Promise((res) => setTimeout(res, 2000));
    }
  };

  // Reattach to an existing job (called on dialog reopen if localStorage
  // has a pending job id). Silently drops the reference if the job is
  // already terminal or 404s.
  const resumePolling = async (id) => {
    try {
      const { data: job } = await api.get(`/equipment-repairs/sync/${id}`);
      const TERMINAL = new Set(["done", "failed", "partial", "aborted"]);
      if (TERMINAL.has(job.status)) {
        if (activeJobKey) localStorage.removeItem(activeJobKey);
        return;
      }
      setRunning(true);
      setJobId(id);
      applyJob(job);
      const final = await pollUntilDone(id);
      if (final && activeJobKey) localStorage.removeItem(activeJobKey);
      setRunning(false);
      setJobId(null);
    } catch {
      if (activeJobKey) localStorage.removeItem(activeJobKey);
    }
  };

  const run = async () => {
    setRunning(true); setSummary(null);
    const days = daysBetween(start, end);
    setProgress({ done: 0, total: days.length, current: null, failed: [] });

    // Job-based flow: POST once → returns 202 with job_id (<1s). Then poll
    // GET /sync/{job_id} every 2s until terminal. This side-steps the
    // preview URL's ~60s edge timeout that used to 502 heavy days.
    let job;
    try {
      const { data } = await api.post("/equipment-repairs/sync", {
        company_id: companyId, start_date: start, end_date: end,
      });
      job = data.job || data;
      setJobId(data.job_id);
      if (activeJobKey) localStorage.setItem(activeJobKey, data.job_id);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "sync_queue_full") {
        toast.warning("Sync is already running with a request queued — wait a minute before retrying.");
      } else {
        toast.error(`Sync failed to start: ${extractError(e)}`);
      }
      setRunning(false);
      return;
    }
    if (job) applyJob(job);

    const final = await pollUntilDone(job.id || job.job_id);
    setRunning(false);
    setJobId(null);
    if (activeJobKey) localStorage.removeItem(activeJobKey);

    if (!final) return;
    const r = final.report || {};
    const failedCount = (r.failed_days || []).length;
    if (final.status === "done") {
      toast.success(`Sync complete · ${r.invoices_new || 0} new · ${r.invoices_updated || 0} updated · ${r.drivers_resolved || 0} drivers`);
    } else if (final.status === "partial") {
      toast.warning(`Sync finished with ${failedCount} day(s) failed`);
    } else if (final.status === "aborted") {
      toast.info(`Sync aborted at ${(final.progress || {}).days_done || 0}/${(final.progress || {}).total_days || 0} days.`);
    } else {
      toast.error(`Sync failed: ${final.error || "unknown error"}`);
    }
    // Optimistic nav badge refresh
    try { window.dispatchEvent(new Event("repairs:refresh-badge")); } catch { /* no-op */ }
    onComplete?.({
      totals: {
        invoices_new: r.invoices_new || 0,
        invoices_updated: r.invoices_updated || 0,
        corrections_new: r.corrections_new || 0,
        drivers_resolved: r.drivers_resolved || 0,
      },
      failed: (r.failed_days || []).map((day) => ({ day })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="repairs-sync-dialog">
        <DialogHeader>
          <DialogTitle>Sync FullBay repair invoices</DialogTitle>
          <DialogDescription>
            Runs a background job — no client timeout. Progress updates day-by-day; you can safely close and reopen this dialog and it will pick up where the job left off.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Company</Label>
            {presetCompany ? (
              <div
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800"
                data-testid="repairs-sync-company"
              >
                <span className="font-mono text-xs text-slate-500 mr-2">{presetCompany.code}</span>
                {presetCompany.fullbay_customer_title || <em className="text-slate-400">unmapped</em>}
              </div>
            ) : (
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger data-testid="repairs-sync-company"><SelectValue placeholder="Select mapped company…" /></SelectTrigger>
                <SelectContent>
                  {eligible.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.code} — {c.fullbay_customer_title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Start date</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="repairs-sync-start" />
            </div>
            <div className="space-y-2">
              <Label>End date</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="repairs-sync-end" />
            </div>
          </div>
          {progress.total > 0 && (
            <div className="rounded-md bg-slate-50 p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium">
                  {progress.done}/{progress.total} days synced
                  {progress.current && ` · fetching ${progress.current}…`}
                </span>
                {running && jobId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={abortJob}
                    disabled={aborting}
                    data-testid="repairs-sync-abort"
                  >
                    {aborting ? "Aborting…" : "Abort"}
                  </Button>
                )}
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded bg-slate-200">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
              {stalled && (
                <div
                  className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
                  data-testid="repairs-sync-stalled-banner"
                >
                  Stalled — last update {stalled.label}. Use Abort to cancel this job, then retry.
                </div>
              )}
              {summary && (
                <div className="mt-3 grid grid-cols-4 gap-2 text-xs" data-testid="repairs-sync-summary">
                  <SummaryCell label="New" value={summary.invoices_new} />
                  <SummaryCell label="Updated" value={summary.invoices_updated} />
                  <SummaryCell label="Corrections" value={summary.corrections_new} />
                  <SummaryCell label="Drivers" value={summary.drivers_resolved} />
                </div>
              )}
              {progress.failed.length > 0 && (
                <div className="mt-3 text-xs text-rose-600">
                  Failed days: {progress.failed.map((f) => f.day).join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={running}>Close</Button>
          <Button onClick={run} disabled={running || !companyId} className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90" data-testid="repairs-sync-run">
            {running ? "Syncing…" : "Start sync"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
